// @flow
import React, { Component } from 'react';
import moment from 'moment';
import { ExtendedFunction, Margins, SvgDimensions, Data } from './index';
import { DataPoint } from '../../store';

moment.locale('fi');

type Path = {
  x1: string;
  x2: string;
  y1: string | number;
  y2: string | number;
  c1: string | number;
  c2: string | number;
};

type TemperatureScales = {
  xScale: ExtendedFunction;
  yScale: ExtendedFunction;
  yScale3: ExtendedFunction;
};

type Props = {
  scales: TemperatureScales;
  data: DataPoint[];
  margins: Margins;
  svgDimensions: SvgDimensions;
  showComparison: boolean;
  level: string;
};

type State = {
  comparisonDataReady: boolean;
};

class Lines extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      comparisonDataReady: false
    };
  }

  render(): JSX.Element {
    const { scales, margins, data, svgDimensions, showComparison, level } = this.props;
    const { xScale, yScale3 } = scales;
    const { height } = svgDimensions;

    const vY = (d: string | number) => yScale3(Number(d) || 0);
    const vPosition = (v: string | number) => height - margins.bottom - yScale3(Number(v) || 0);

    const barWidth = xScale.bandwidth() / 2;

    const datapointHasComparison = (datapoint: DataPoint): number => {
      if (datapoint.comparison?.ts !== undefined && datapoint.comparison.v !== undefined) {
        return Number(datapoint.comparison.v);
      }

      return 0;
    };

    const isPastOrCurrentPoint = (ts: string): boolean => {
      return moment(ts).isSameOrBefore(moment(), 'month');
    };

    const mapPaths = (chartData: Data): Path[] => {
      let i = 0;
      const paths = [];
      let path;

      while (i < chartData.length) {
        path = {
          x1: chartData[i].ts,
          x2: chartData[i + 1] ? chartData[i + 1].ts : chartData[i].ts,
          y1: chartData[i].v,
          y2: chartData[i + 1] ? chartData[i + 1].v : chartData[i].v,
          c1: datapointHasComparison(chartData[i]),
          c2: chartData[i + 1] ? datapointHasComparison(chartData[i + 1]) : datapointHasComparison(chartData[i])
        };

        paths.push(path);
        i++;
      }

      return paths;
    };

    const paths: Path[] = mapPaths(data);

    let lines = null;

    if (data.length > 0 && paths && level !== 'quarter-hour') {
      lines = paths.map((d: Path, i) => {
        const showPoint = isPastOrCurrentPoint(d.x1);
        const showLineToNext = isPastOrCurrentPoint(d.x1) && isPastOrCurrentPoint(d.x2);
        const hasComparisonPoint = showComparison && d.c1 !== 0 && d.c2 !== 0 && showLineToNext;

        const pointX = Number(xScale(d.x1)) + barWidth;
        const nextPointX = Number(xScale(d.x2)) + barWidth;
        const pointY = Number(vY(d.y1));
        const nextPointY = Number(vY(d.y2));
        const comparisonY = Number(vY(d.c1));
        const comparisonNextY = Number(vY(d.c2));

        return (
          <g key={i}>
            {showLineToNext && (
              <line
                className="graph-lines"
                x1={pointX}
                x2={nextPointX}
                y1={pointY}
                y2={nextPointY}
                height={vPosition(d.y1)}
                strokeWidth={3}
              />
            )}

            {showPoint && (
              <g className="line-point-group">
                <circle className="graph-circles" cx={pointX} cy={pointY} r={4} />
              </g>
            )}

            {hasComparisonPoint && (
              <>
                <line
                  className="graph-lines2"
                  x1={pointX}
                  x2={nextPointX}
                  y1={comparisonY}
                  y2={comparisonNextY}
                  height={vPosition(d.c1)}
                  strokeWidth={3}
                  strokeDasharray="8,2"
                />

                <rect className="graph-circles2" x={pointX - 4} y={comparisonY - 4} width={7} height={7} />
              </>
            )}
          </g>
        );
      });
    }

    return <g>{lines}</g>;
  }
}

export default Lines;
